// src/constants/macro/overviewIndicatorsColumns.tsx
/**
 * Column definitions for Macro Overview Indicators tables.
 * Uses the standard SortableColumn pattern for DataTable compatibility.
 */

import { percentColumn } from "@/components/tables";
import type { SortableColumn } from "@/components/tables";
import type { OverviewIndicatorsDisplayRow } from "@/types/dashboards/macro";

/**
 * Indicator name column (first column, pinned left equivalent)
 */
const indicatorColumn: SortableColumn<OverviewIndicatorsDisplayRow> = {
  id: "indicator",
  header: "Indicator",
  align: "left",
  minWidth: 200,
  cell: (row: OverviewIndicatorsDisplayRow) => (
    <span className="text-slate-100 font-medium">{row.indicator}</span>
  ),
  sortValue: (row: OverviewIndicatorsDisplayRow) => row.indicator,
};

/**
 * Current value column - displays the current value with green color
 */
const currentColumn: SortableColumn<OverviewIndicatorsDisplayRow> = {
  id: "current",
  header: "Current",
  align: "right",
  cell: (row: OverviewIndicatorsDisplayRow) => {
    if (row.current == null || row.current === "") {
      return <span className="text-slate-400">–</span>;
    }
    return (
      <span className="tabular-nums text-emerald-400 font-medium">
        {row.current}
      </span>
    );
  },
  sortValue: (row: OverviewIndicatorsDisplayRow) => {
    if (row.current == null || row.current === "") return -Infinity;
    // Extract numeric value from formatted string
    const cleaned = row.current.replace(/[^0-9.-]/g, "");
    const num = parseFloat(cleaned);
    return isNaN(num) ? -Infinity : num;
  },
};

/**
 * MSI (Market Sentiment Indicator) column with color coding
 */
const msiColumn: SortableColumn<OverviewIndicatorsDisplayRow> = {
  id: "msi",
  header: "MSI",
  align: "center",
  minWidth: 80,
  cell: (row: OverviewIndicatorsDisplayRow) => {
    if (!row.msi) {
      return <span className="text-slate-400">–</span>;
    }
    const colorClass =
      row.msi === "Bullish"
        ? "text-emerald-400"
        : row.msi === "Bearish"
        ? "text-rose-400"
        : "text-slate-300";
    return <span className={`font-medium ${colorClass}`}>{row.msi}</span>;
  },
  sortValue: (row: OverviewIndicatorsDisplayRow) => row.msi ?? "",
};

/**
 * Complete column definitions for Overview Indicators tables
 */
export const OVERVIEW_INDICATORS_COLUMNS: SortableColumn<OverviewIndicatorsDisplayRow>[] =
  [
    indicatorColumn,
    currentColumn,
    msiColumn,
    percentColumn<OverviewIndicatorsDisplayRow>({
      id: "change_1m",
      header: "1M %",
      accessor: (r) => r.change_1m,
      alreadyPercent: true,
      decimals: 2,
    }),
    percentColumn<OverviewIndicatorsDisplayRow>({
      id: "change_3m",
      header: "3M %",
      accessor: (r) => r.change_3m,
      alreadyPercent: true,
      decimals: 2,
    }),
    percentColumn<OverviewIndicatorsDisplayRow>({
      id: "change_6m",
      header: "6M %",
      accessor: (r) => r.change_6m,
      alreadyPercent: true,
      decimals: 2,
    }),
    percentColumn<OverviewIndicatorsDisplayRow>({
      id: "change_1y",
      header: "1Y %",
      accessor: (r) => r.change_1y,
      alreadyPercent: true,
      decimals: 2,
    }),
    percentColumn<OverviewIndicatorsDisplayRow>({
      id: "change_4y",
      header: "4Y %",
      accessor: (r) => r.change_4y,
      alreadyPercent: true,
      decimals: 2,
    }),
  ];
